import { memo } from "react";
import { NavLink } from "react-router-dom";

import { useTranslate } from "@/hooks";
import { resolveTranslatedLabel } from "@/utils";

import { SectionContainer, SectionLink, SectionTitle } from "./styles";

export interface FooterColumn {
  id: string;
  title: string;
  items: { id: string; title: string; link: string }[];
}

interface LinkSectionProps {
  section: FooterColumn;
}

const resolveFooterLabel = (translate: ReturnType<typeof useTranslate>, name: string): string =>
  resolveTranslatedLabel(translate, name, "footer");

/** A social profile, a mail address: anything the router would mistake for a page of ours. */
const isExternalLink = (link: string) => /^(https?:)?\/\/|^(mailto|tel):/i.test(link);

export const LinkSection = memo(({ section }: LinkSectionProps) => {
  const translate = useTranslate();

  return (
    <SectionContainer>
      <SectionTitle variant="body2" component="h3">
        {resolveFooterLabel(translate, section.title)}
      </SectionTitle>
      {section.items.map((item) =>
        isExternalLink(item.link) ? (
          <SectionLink
            key={item.id}
            variant="body2"
            component="a"
            href={item.link}
            target="_blank"
            rel="noopener noreferrer"
          >
            {resolveFooterLabel(translate, item.title)}
          </SectionLink>
        ) : (
          <SectionLink key={item.id} variant="body2" component={NavLink} to={item.link || "#"}>
            {resolveFooterLabel(translate, item.title)}
          </SectionLink>
        ),
      )}
    </SectionContainer>
  );
});
LinkSection.displayName = "LinkSection";
